import {
  diffWorkflows,
  parseSkillMd,
  serializeSkillMd,
  updateStepFields,
  type Issue,
  type Workflow,
  type WorkflowDiff,
} from '@nexus/skill-workflow';

import { START_NODE_ID } from './canvas-graph';

export type EditSource = 'chat' | 'inline';

export interface ChatEntry {
  role: 'user' | 'assistant';
  text: string;
  applied?: boolean;
  failed?: boolean;
  /** Ari's answer to Level up: what it learned and what it proposes. Nothing is changed yet. */
  levelUp?: 'proposal';
}

/** Where the owner is in a Level up: Ari has proposed changes, then an edit has followed. */
export type LevelUpPhase = 'none' | 'proposed' | 'applied';

/** Read from the chat, so undo and redo move it too. `folded` is the chat at the last Level up publish. */
export function levelUpPhaseOf(chat: ChatEntry[], folded: readonly ChatEntry[]): LevelUpPhase {
  let shared = 0;
  while (shared < chat.length && chat[shared] === folded[shared]) shared++;
  const fresh = chat.slice(shared);
  const proposal = fresh.findIndex((entry) => entry.levelUp === 'proposal');
  if (proposal < 0) return 'none';
  return fresh.slice(proposal + 1).some((entry) => entry.applied) ? 'applied' : 'proposed';
}

interface Snapshot {
  frontmatter: Record<string, unknown>;
  prose: string;
  workflow: Workflow;
  chat: ChatEntry[];
}

export interface WorkflowEditorState extends Snapshot {
  parseIssues: Issue[];
  history: Snapshot[];
  future: Snapshot[];
  chatPending: boolean;
  chatTurnStart: number;
  /** The skill as it was when the author sent the pending message. */
  turnBase: Snapshot | null;
  highlights: WorkflowDiff;
  selectedStepId: string | null;
  pinnedStepIds: string[];
  lastEditSource: EditSource | null;
}

export type WorkflowEditorAction =
  | { type: 'apply'; workflow: Workflow; source: EditSource }
  | { type: 'applyMd'; skillMd: string; source: EditSource }
  | { type: 'updateFields'; stepId: string; patch: Record<string, unknown> }
  | { type: 'updateSkillText'; name?: string; description?: string; prose?: string }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'chatSend'; text: string }
  | { type: 'chatReply'; text: string; skillMd?: string; workflow?: Workflow; levelUp?: ChatEntry['levelUp'] }
  | { type: 'chatProgress'; skillMd: string }
  | { type: 'chatFail'; text: string }
  | { type: 'select'; stepId: string | null }
  | { type: 'togglePin'; stepId: string }
  | { type: 'clearPins' };

const NO_HIGHLIGHTS: WorkflowDiff = { added: [], changed: [], removed: [] };
const UNREADABLE = new Set(['bad_frontmatter', 'bad_yaml', 'multiple_workflow_blocks']);

export function hasSteps(workflow: Workflow): boolean {
  return workflow.sections.some((section) => section.steps.length > 0);
}

/** A new skill with no steps, no instructions and no chat. Leaving it should not keep it. */
export function isUntouched(state: WorkflowEditorState): boolean {
  return !hasSteps(state.workflow) && !state.prose.trim() && state.chat.length === 0;
}

export function skillMdOf(state: Pick<WorkflowEditorState, 'frontmatter' | 'prose' | 'workflow'>): string {
  return serializeSkillMd({
    frontmatter: state.frontmatter,
    prose: state.prose,
    workflow: hasSteps(state.workflow) ? state.workflow : null,
  });
}

export function initWorkflowEditor(skillMd: string): WorkflowEditorState {
  const parsed = parseSkillMd(skillMd);
  return {
    frontmatter: parsed.frontmatter,
    prose: parsed.prose,
    workflow: parsed.workflow ?? { variables: {}, sections: [] },
    chat: [],
    parseIssues: parsed.issues,
    history: [],
    future: [],
    chatPending: false,
    chatTurnStart: 0,
    turnBase: null,
    highlights: NO_HIGHLIGHTS,
    selectedStepId: null,
    pinnedStepIds: [],
    lastEditSource: null,
  };
}

function stepExists(workflow: Workflow, stepId: string): boolean {
  if (stepId === START_NODE_ID) return true;
  return diffWorkflows(null, workflow).added.includes(stepId);
}

function snapshotOf(state: Snapshot): Snapshot {
  return { frontmatter: state.frontmatter, prose: state.prose, workflow: state.workflow, chat: state.chat };
}

function sameText(a: Snapshot, b: Snapshot): boolean {
  return a.prose === b.prose && JSON.stringify(a.frontmatter) === JSON.stringify(b.frontmatter);
}

function apply(
  state: WorkflowEditorState,
  next: Snapshot,
  source: EditSource,
  before: Snapshot = snapshotOf(state)
): WorkflowEditorState {
  const highlights = diffWorkflows(state.workflow, next.workflow);
  // The step diff ignores order, sections and variables, so compare the whole workflow to find a real no-op.
  const workflowChanged = JSON.stringify(state.workflow) !== JSON.stringify(next.workflow);
  if (!workflowChanged && sameText(state, next)) return state;
  return {
    ...state,
    ...next,
    history: [...state.history, before],
    future: [],
    highlights: source === 'inline' ? NO_HIGHLIGHTS : highlights,
    selectedStepId:
      state.selectedStepId && stepExists(next.workflow, state.selectedStepId) ? state.selectedStepId : null,
    pinnedStepIds: state.pinnedStepIds.filter((id) => stepExists(next.workflow, id)),
    lastEditSource: source,
  };
}

function snapshotFromMd(state: WorkflowEditorState, skillMd: string): Snapshot | null {
  const parsed = parseSkillMd(skillMd);
  if (parsed.issues.some((i) => UNREADABLE.has(i.code))) return null;
  const workflow = parsed.workflow ?? { variables: {}, sections: [] };
  return { frontmatter: parsed.frontmatter, prose: parsed.prose, workflow, chat: state.chat };
}

function restore(state: WorkflowEditorState, target: Snapshot, history: Snapshot[], future: Snapshot[]) {
  return {
    ...state,
    ...target,
    history,
    future,
    highlights: diffWorkflows(state.workflow, target.workflow),
    selectedStepId:
      state.selectedStepId && stepExists(target.workflow, state.selectedStepId) ? state.selectedStepId : null,
    pinnedStepIds: state.pinnedStepIds.filter((id) => stepExists(target.workflow, id)),
    lastEditSource: null,
  };
}

function atTurnBase(state: WorkflowEditorState): WorkflowEditorState {
  const base = state.turnBase;
  const content = base ? { frontmatter: base.frontmatter, prose: base.prose, workflow: base.workflow } : {};
  return { ...state, ...content, chatPending: false, turnBase: null };
}

function chatReply(
  state: WorkflowEditorState,
  action: Extract<WorkflowEditorAction, { type: 'chatReply' }>
): WorkflowEditorState {
  const replied = atTurnBase(state);
  const proposed = action.skillMd
    ? snapshotFromMd(state, action.skillMd)
    : action.workflow
    ? { ...snapshotOf(state), workflow: action.workflow }
    : null;
  const reply: ChatEntry = { role: 'assistant', text: action.text, ...(action.levelUp && { levelUp: action.levelUp }) };
  if (proposed) {
    const beforeTurn = { ...snapshotOf(replied), chat: state.chat.slice(0, state.chatTurnStart) };
    const next = apply(
      replied,
      { ...proposed, chat: [...state.chat, { ...reply, applied: true }] },
      'chat',
      beforeTurn
    );
    if (next !== replied) return { ...next, pinnedStepIds: [] };
  }
  return { ...replied, chat: [...state.chat, reply] };
}

const HAND_EDITS = new Set(['apply', 'applyMd', 'updateFields', 'updateSkillText']);

export function workflowEditorReducer(state: WorkflowEditorState, action: WorkflowEditorAction): WorkflowEditorState {
  // Ari's reply replaces the whole turn, so an edit made meanwhile would be lost.
  if (state.chatPending && HAND_EDITS.has(action.type)) return state;
  switch (action.type) {
    case 'apply':
      return apply(state, { ...snapshotOf(state), workflow: action.workflow }, action.source);
    case 'applyMd': {
      const next = snapshotFromMd(state, action.skillMd);
      return next ? apply(state, next, action.source) : state;
    }
    case 'updateFields':
      return apply(
        state,
        { ...snapshotOf(state), workflow: updateStepFields(state.workflow, action.stepId, action.patch) },
        'inline'
      );
    case 'updateSkillText':
      return apply(
        state,
        {
          ...snapshotOf(state),
          frontmatter: {
            ...state.frontmatter,
            ...(action.name !== undefined && { name: action.name }),
            ...(action.description !== undefined && { description: action.description }),
          },
          prose: action.prose ?? state.prose,
        },
        'inline'
      );
    case 'undo': {
      const previous = state.history[state.history.length - 1];
      if (!previous || state.chatPending) return state;
      return restore(state, previous, state.history.slice(0, -1), [snapshotOf(state), ...state.future]);
    }
    case 'redo': {
      const [next, ...rest] = state.future;
      if (!next || state.chatPending) return state;
      return restore(state, next, [...state.history, snapshotOf(state)], rest);
    }
    case 'chatSend':
      return {
        ...state,
        chat: [...state.chat, { role: 'user', text: action.text }],
        chatTurnStart: state.chat.length,
        chatPending: true,
        turnBase: snapshotOf(state),
        future: [],
      };
    case 'chatProgress': {
      const shown = state.chatPending ? snapshotFromMd(state, action.skillMd) : null;
      return shown ? { ...state, frontmatter: shown.frontmatter, prose: shown.prose, workflow: shown.workflow } : state;
    }
    case 'chatReply':
      return chatReply(state, action);
    case 'chatFail':
      return {
        ...atTurnBase(state),
        chat: [...state.chat, { role: 'assistant', text: action.text, failed: true }],
      };
    case 'select':
      return { ...state, selectedStepId: action.stepId, highlights: NO_HIGHLIGHTS };
    case 'togglePin':
      return {
        ...state,
        pinnedStepIds: state.pinnedStepIds.includes(action.stepId)
          ? state.pinnedStepIds.filter((id) => id !== action.stepId)
          : [...state.pinnedStepIds, action.stepId],
      };
    case 'clearPins':
      return { ...state, pinnedStepIds: [] };
  }
}
